/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { Button } from 'react-bootstrap'
import Modal from '../../../../components/Modal'
import { Select, OptionType } from '../../../../components/Select/Select'
import LevelSelection from './LevelSelection'
import PatientSearch from './patient-selection/PatientSearch'
import PatientsLevelSelections from './patient-selection/PatientsLevelSelections'
import PatientSelectionTable from './patient-selection/PatientSelectionTable'
import { PublishContext } from './publish-context/PublishContext'
import { eosApiService } from '../../../../services/api/api.eos'
import { toast } from '../../../../utils/toast'
import Utils from '../../../../lib/Utils'
import type { SelectionsData, StudyData, SiteData, SelectOption, DocumentIdEntry } from './types'
import type { SingleValue, MultiValue } from 'react-select'

const initialSelections: SelectionsData = {
  studyFolderId: 0,
  siteFolderIds: [],
  documentIds: [],
  selectionLevel: 'STUDY',
  patientsSelectionLevel: 'ALL',
  searchString: null,
  allPatientsSelected: false,
  allSitesSelected: false,
  filter: {
    pageStart: 1,
    pageSize: 10
  },
  allSitesData: []
}

interface PdrPublishModalProps {
  onClose?: () => void
}

const PdrPublishModal: React.FC<PdrPublishModalProps> = ({ onClose }) => {
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState<boolean>(true)
  const [selectionsData, setSelectionsData] = useState<SelectionsData>(initialSelections)
  const [studies, setStudies] = useState<StudyData[]>([])
  const [sites, setSites] = useState<SiteData[]>([])

  useEffect(() => {
    eosApiService.getPdrStudies().then((data) => {
      setStudies(data as StudyData[])
    })
  }, [])

  const getSites = (studyFolderId: number) => {
    eosApiService.getPdrSites(studyFolderId).then((data) => {
      const sitesData = data as SiteData[]
      setSites(sitesData)
      setSelectionsData((prev) => ({ ...prev, allSitesData: sitesData }))
    })
  }

  const disablePublish = (): boolean => {
    switch (selectionsData?.selectionLevel) {
      case 'STUDY':
        return !selectionsData?.studyFolderId
      case 'SITE':
        return !selectionsData?.siteFolderIds?.length
      default:
        if (selectionsData?.patientsSelectionLevel === 'ALL') {
          return !selectionsData?.siteFolderIds?.length
        } else {
          return !selectionsData?.documentIds?.length
        }
    }
  }

  const publishToMyMedidata = () => {
    const requestObject: {
      studyFolderId: number
      siteFolderIds: number[]
      documentIds: number[]
    } = {
      studyFolderId: 0,
      siteFolderIds: [],
      documentIds: []
    }

    // Build request: study is always required; site and document are additive based on level
    requestObject.studyFolderId = selectionsData?.studyFolderId

    if (selectionsData?.selectionLevel === 'SITE' || selectionsData?.selectionLevel === 'PATIENT') {
      requestObject.siteFolderIds = selectionsData?.allSitesSelected
        ? []
        : selectionsData?.selectionLevel === 'PATIENT'
          ? selectionsData?.patientsSelectionLevel === 'ALL'
            ? selectionsData?.siteFolderIds
            : _.uniq(_.map(selectionsData?.documentIds, 'site'))
          : selectionsData?.siteFolderIds

      if (selectionsData?.selectionLevel === 'PATIENT' && selectionsData?.patientsSelectionLevel !== 'ALL') {
        requestObject.siteFolderIds = _.uniq(_.map(selectionsData?.documentIds, 'site'))
      }
    }

    if (selectionsData?.selectionLevel === 'PATIENT') {
      if (selectionsData?.patientsSelectionLevel === 'ALL') {
        requestObject.documentIds = []
      } else {
        requestObject.documentIds = selectionsData?.allPatientsSelected
          ? []
          : _.uniq(_.map(selectionsData?.documentIds, 'document'))
      }
    }

    eosApiService.publishToMyMedidata(requestObject).then(
      () => {
        toast.success(translate('PDR_PUBLISH_SUCCESS'))
        setIsOpen(false)
        onClose?.()
      },
      (error: { data?: { message?: string } }) => {
        toast.error(error?.data?.message ? error.data.message : translate('ERROR'))
      }
    )
  }

  const getSiteOptions = (): SelectOption[] => {
    if (sites?.length) {
      return [
        { value: -1, label: translate('ETMF_LABEL_SELECT_ALL_SITES') },
        ...Utils.transformToSelectOptions(sites, 'siteFolderId', 'siteFolderName')
      ]
    } else {
      return []
    }
  }

  const doClose = () => {
    setIsOpen(false)
    onClose?.()
  }

  const handleStudyChange = (option: SingleValue<OptionType> | MultiValue<OptionType>) => {
    if (option != null && !Array.isArray(option)) {
      const singleOption = option as OptionType
      const selections = _.cloneDeep(selectionsData)
      selections.studyFolderId = singleOption.value
      selections.siteFolderIds = []
      selections.documentIds = []
      setSelectionsData(selections)
      getSites(singleOption.value)
    } else {
      setSelectionsData((data) => ({
        ...data,
        studyFolderId: 0,
        siteFolderIds: [],
        documentIds: []
      }))
      setSites([])
    }
  }

  const handleSiteChange = (option: SingleValue<OptionType> | MultiValue<OptionType>) => {
    const selections = _.cloneDeep(selectionsData)
    selections.allSitesSelected = false
    const optionArray = (Array.isArray(option) ? option : []) as OptionType[]
    const selectAllLabel = translate('ETMF_LABEL_SELECT_ALL_SITES')

    if (optionArray.length === 0) {
      selections.siteFolderIds = []
    } else {
      const firstIsSelectAll = optionArray[0].label === selectAllLabel
      const lastIsSelectAll = optionArray[optionArray.length - 1].label === selectAllLabel

      if (firstIsSelectAll && lastIsSelectAll) {
        selections.siteFolderIds = optionArray.map((item) => item.value)
        selections.allSitesSelected = true
      } else if (firstIsSelectAll && !lastIsSelectAll) {
        const filtered = optionArray.filter((item) => item.label !== selectAllLabel)
        selections.siteFolderIds = filtered.map((item) => item.value)
      } else if (!firstIsSelectAll && lastIsSelectAll) {
        selections.siteFolderIds = [optionArray[optionArray.length - 1].value]
        selections.allSitesSelected = true
      } else {
        selections.siteFolderIds = optionArray.map((item) => item.value)
      }
    }

    selections.searchString = ''
    selections.documentIds = []
    setSelectionsData(selections)
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      id="modal-add-file"
      className="standard-modal-size-container publish-to-mymedidata"
      title={`${translate('RIGHT_TOP_NAV_EOS_PDR')} - ${translate('PDR_PUBLISH')}`}
      size="lg"
    >
      <Modal.Body>
        <PublishContext.Provider value={{ selectionsData, setSelectionsData }}>
          <div>
            <LevelSelection />
          </div>
          <div className="data-selection-wrapper">
            <div className="study-selection level-data-selection">
              <label className="control-label">{translate('EMTF_MD_STUDY')}</label>
              <Select
                id="d3ecb17b-c1ed-4730-a257-faf53ed30d2d"
                options={Utils.transformToSelectOptions(studies, 'studyFolderId', 'studyName')}
                value={selectionsData?.studyFolderId}
                onChange={handleStudyChange}
                searchable
                placeholder={translate('EOS_SETUP_SELECT_STUDY...')}
              />
            </div>
            {(selectionsData?.selectionLevel === 'PATIENT' || selectionsData?.selectionLevel === 'SITE') && (
              <div className="site-selection level-data-selection">
                <label className={`control-label ${!selectionsData?.studyFolderId ? 'disabled-label' : ''}`}>
                  {translate('SITE(S)')}
                </label>
                <Select
                  id="5bd7c15c-3082-4dcd-8a68-962ce33286fa"
                  options={getSiteOptions()}
                  value={selectionsData?.siteFolderIds}
                  onChange={handleSiteChange}
                  searchable
                  multi
                  placeholder={
                    <span className={!selectionsData?.studyFolderId ? 'disabled-label' : ''}>
                      {translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP6')}
                    </span>
                  }
                  isDisabled={!selectionsData?.studyFolderId}
                />
              </div>
            )}
          </div>
          {selectionsData?.selectionLevel === 'PATIENT' && (
            <div className="patient-selection level-data-selection">
              <PatientsLevelSelections />
              {selectionsData?.patientsSelectionLevel === 'CUSTOM' && !!selectionsData?.siteFolderIds?.length && (
                <div>
                  <div className="patient-search">
                    <PatientSearch
                      search={(textData) => {
                        setSelectionsData((data) => ({
                          ...data,
                          searchString: textData?.length ? textData : null
                        }))
                      }}
                    />
                  </div>
                  <div className="theme-sandman">
                    <PatientSelectionTable />
                  </div>
                </div>
              )}
            </div>
          )}
        </PublishContext.Provider>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn btn-link" onClick={doClose}>
          {translate('CANCEL')}
        </Button>
        <Button disabled={disablePublish()} className="btn btn-primary" onClick={publishToMyMedidata}>
          {translate('PDR_PUBLISH_ACTION')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PdrPublishModal
